import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wrench, Plus, Search, ClipboardCheck, Thermometer, CheckCircle2, ListPlus } from "lucide-react";

const EMPTY = {
  name: "", equipment_type: "Tissue processor", manufacturer: "", model: "", serial_number: "",
  asset_tag: "", location: "", department: "Histology", service_start_date: "", vendor: "",
  vendor_contact: "", contract_number: "", contract_expiry: "", status: "In service",
  tracks_reagent_changes: false, temp_min: "", temp_max: "", temp_unit: "°C", notes: "",
};

export default function Equipment() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [due, setDue] = useState([]);
  const [filters, setFilters] = useState({ q: "", equipment_type: "", department: "", alert: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState("");
  const [checkTask, setCheckTask] = useState(null);
  const [checkForm, setCheckForm] = useState({ comments: "", temperature: "" });

  const load = useCallback(async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const [list, sum, pm] = await Promise.all([
        api.get("/equipment", { params }),
        api.get("/equipment/summary"),
        api.get("/equipment/pm-due"),
      ]);
      setRows(list.data);
      setSummary(sum.data);
      setDue(pm.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/equipment/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = user?.role !== "viewer";

  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const body = { ...form };
      ["temp_min", "temp_max"].forEach((k) => { body[k] = body[k] === "" ? null : Number(body[k]); });
      ["service_start_date", "contract_expiry"].forEach((k) => { if (!body[k]) body[k] = null; });
      const { data } = await api.post("/equipment", body);
      toast.success(`${data.name} added to the register`);
      setOpen(false);
      setForm({ ...EMPTY });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const completeCheck = async (e) => {
    e.preventDefault();
    setBusy("check");
    try {
      const { data } = await api.post(`/equipment/pm-tasks/${checkTask.id}/complete`, {
        comments: checkForm.comments,
        temperature: checkForm.temperature === "" ? null : Number(checkForm.temperature),
      });
      if (data.completion.temp_out_of_range) toast.error("Logged — temperature is OUT OF RANGE");
      else toast.success("Check logged");
      setCheckTask(null);
      setCheckForm({ comments: "", temperature: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const loadStarters = async () => {
    setBusy("starters");
    try {
      const { data } = await api.post("/equipment/load-starters");
      toast.success(`${data.created.length} instruments loaded with their PM schedules`
        + (data.skipped.length ? ` · ${data.skipped.length} already existed` : ""));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const Alert = ({ label, value, tone, testid, alert }) => (
    <button onClick={() => setFilters((f) => ({ ...f, alert: f.alert === alert ? "" : alert }))}
            data-testid={testid}
            className={`border p-3 text-left transition-colors ${filters.alert === alert ? "border-[#134e4a] bg-[#134e4a]/5" : "border-slate-200 bg-white hover:border-[#134e4a]"}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`num mt-1 text-2xl font-bold ${value ? tone : "text-slate-900"}`}>{value}</div>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">TechTraX</p>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <Wrench size={26} className="text-[#134e4a]" /> Equipment Maintenance
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Instrument register, preventive-maintenance schedules, service history, reagent changes and calibration records.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Link to="/equipment/temperatures" data-testid="temperature-trends-link">
            <Button variant="outline" className="h-11 border-slate-300">
              <Thermometer size={16} className="mr-2" /> Temperature trends
            </Button>
          </Link>
          {canEdit && (
            <Button onClick={() => setOpen(true)} data-testid="new-equipment-button"
                    className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
              <Plus size={16} className="mr-2" /> Add instrument
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="equipment-alerts">
          <Alert label="PM due today" value={summary.pm_due_today} tone="text-amber-700" testid="equip-alert-due-today" alert="pm_due_today" />
          <Alert label="PM overdue" value={summary.pm_overdue} tone="text-red-800" testid="equip-alert-overdue" alert="pm_overdue" />
          <Alert label="Out of service" value={summary.out_of_service} tone="text-red-800" testid="equip-alert-out-of-service" alert="out_of_service" />
          <Alert label={`Contract ≤${summary.contract_warn_days}d`} value={summary.contract_expiring} tone="text-amber-700" testid="equip-alert-contract" alert="contract_expiring" />
          <Alert label="No service 12 mo" value={summary.service_stale} tone="text-amber-700" testid="equip-alert-stale" alert="service_stale" />
        </div>
      )}

      {due.length > 0 && (
        <section className="border border-amber-300 bg-amber-50/60" data-testid="pm-due-checklist">
          <header className="flex items-center gap-2 border-b border-amber-200 px-3 py-2">
            <ClipboardCheck size={15} className="text-amber-800" />
            <h2 className="text-base font-semibold text-amber-900 md:text-lg">Bench checklist — {due.length} check(s) due</h2>
          </header>
          <ul className="divide-y divide-amber-200">
            {due.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm" data-testid={`pm-due-${t.id}`}>
                <span className="font-semibold text-slate-900">{t.task}</span>
                <Link to={`/equipment/${t.equipment_id}`} className="text-[#134e4a] hover:underline">{t.equipment_name}</Link>
                <span className="text-xs text-slate-600">{t.frequency}{t.location ? ` · ${t.location}` : ""}</span>
                {t.records_temperature && (
                  <span className="inline-flex items-center gap-1 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">
                    <Thermometer size={11} /> {t.temp_min ?? "—"} to {t.temp_max ?? "—"}{t.temp_unit}
                  </span>
                )}
                    <span className={`ml-auto text-xs font-semibold ${t.overdue ? "text-red-800" : "text-amber-800"}`}>
                      {t.overdue ? `${Math.abs(t.days_until_due)}d overdue` : "due today"}
                    </span>
                {canEdit && (
                  <Button size="sm" onClick={() => { setCheckTask(t); setCheckForm({ comments: "", temperature: "" }); }}
                          data-testid={`pm-complete-${t.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                    <CheckCircle2 size={14} className="mr-1" /> Log done
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   data-testid="equipment-search-input" placeholder="Name, model, serial or asset tag"
                   className="h-11 border-slate-300 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Type</Label>
          <select value={filters.equipment_type} onChange={(e) => setFilters((f) => ({ ...f, equipment_type: e.target.value }))}
                  data-testid="equipment-type-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All types</option>
            {(meta?.equipment_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
          <select value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  data-testid="equipment-department-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All departments</option>
            {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <Button variant="outline" onClick={() => setFilters({ q: "", equipment_type: "", department: "", alert: "" })}
                data-testid="equipment-clear-filters" className="h-11 border-slate-300">Clear</Button>
      </div>

      <div className="border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm" data-testid="equipment-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">Instrument</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Serial / asset</th><th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">PM tasks</th>
                <th className="px-3 py-2">Last service</th><th className="px-3 py-2">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`equipment-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/equipment/${r.id}`} className="font-semibold text-[#134e4a] hover:underline"
                          data-testid={`equipment-link-${r.id}`}>{r.name}</Link>
                    <div className="text-[11px] text-slate-500">{r.manufacturer} {r.model}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.equipment_type}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.serial_number || "—"}{r.asset_tag ? ` / ${r.asset_tag}` : ""}</td>
                  <td className="px-3 py-2 text-slate-600">{r.location || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${r.status === "In service" ? "bg-[#134e4a] text-white" : r.status === "Out of service" ? "bg-red-800 text-white" : "bg-slate-300 text-slate-800"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="num px-3 py-2 text-right text-slate-700">{r.pm_task_count}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.last_service_at ? fmtDateTime(r.last_service_at) : "never"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.pm_overdue > 0 && <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-semibold text-white" data-testid={`equip-flag-overdue-${r.id}`}>{r.pm_overdue} PM overdue</span>}
                      {r.pm_due_today > 0 && <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">{r.pm_due_today} due today</span>}
                      {r.contract_expiring && <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900" data-testid={`equip-flag-contract-${r.id}`}>Contract {r.contract_days_left}d</span>}
                      {r.service_stale && <span className="bg-slate-200 px-1.5 py-0.5 text-[11px] font-semibold text-slate-800" data-testid={`equip-flag-stale-${r.id}`}>No service 12 mo</span>}
                      {!r.pm_overdue && !r.pm_due_today && !r.contract_expiring && !r.service_stale && <span className="text-[11px] text-slate-400">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="px-3 py-10 text-center" data-testid="equipment-empty">
            <Wrench size={18} className="mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">
              {filters.q || filters.equipment_type || filters.department || filters.alert
                ? "No instruments match these filters."
                : "No instruments in the register yet."}
            </p>
            {canEdit && (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Button onClick={() => setOpen(true)} data-testid="empty-add-equipment-button"
                        className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                  <Plus size={16} className="mr-2" /> Add your first instrument
                </Button>
                <Button variant="outline" disabled={busy === "starters"} onClick={loadStarters}
                        data-testid="load-starters-button" className="h-11 border-slate-300">
                  <ListPlus size={16} className="mr-2" />
                  {busy === "starters" ? "Loading…" : "Load starter list"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-equipment-dialog">
          <DialogHeader>
            <DialogTitle>Add instrument</DialogTitle>
            <DialogDescription>Register the instrument, then add its PM schedule and log service on its page.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     data-testid="equipment-name-input" required minLength={2}
                     placeholder="e.g. Tissue Processor 1" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Type</Label>
                <select value={form.equipment_type} onChange={(e) => setForm((f) => ({ ...f, equipment_type: e.target.value }))}
                        data-testid="equipment-type-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.equipment_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
                <select value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                        data-testid="equipment-department-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Manufacturer</Label>
                <Input value={form.manufacturer} onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))}
                       data-testid="equipment-manufacturer-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Model</Label>
                <Input value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
                       data-testid="equipment-model-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Serial number</Label>
                <Input value={form.serial_number} onChange={(e) => setForm((f) => ({ ...f, serial_number: e.target.value }))}
                       data-testid="equipment-serial-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Asset tag</Label>
                <Input value={form.asset_tag} onChange={(e) => setForm((f) => ({ ...f, asset_tag: e.target.value }))}
                       data-testid="equipment-asset-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Location</Label>
                <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                       data-testid="equipment-location-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">In service since</Label>
                <Input type="date" value={form.service_start_date} onChange={(e) => setForm((f) => ({ ...f, service_start_date: e.target.value }))}
                       data-testid="equipment-start-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Service contract expiry</Label>
                <Input type="date" value={form.contract_expiry} onChange={(e) => setForm((f) => ({ ...f, contract_expiry: e.target.value }))}
                       data-testid="equipment-contract-expiry-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                       data-testid="equipment-vendor-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Vendor contact</Label>
                <Input value={form.vendor_contact} onChange={(e) => setForm((f) => ({ ...f, vendor_contact: e.target.value }))}
                       data-testid="equipment-vendor-contact-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Contract number</Label>
                <Input value={form.contract_number} onChange={(e) => setForm((f) => ({ ...f, contract_number: e.target.value }))}
                       data-testid="equipment-contract-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Temp min (optional)</Label>
                <Input type="number" step="any" value={form.temp_min} onChange={(e) => setForm((f) => ({ ...f, temp_min: e.target.value }))}
                       data-testid="equipment-temp-min-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Temp max (optional)</Label>
                <Input type="number" step="any" value={form.temp_max} onChange={(e) => setForm((f) => ({ ...f, temp_max: e.target.value }))}
                       data-testid="equipment-temp-max-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Unit</Label>
                <select value={form.temp_unit} onChange={(e) => setForm((f) => ({ ...f, temp_unit: e.target.value }))}
                        data-testid="equipment-temp-unit-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  <option value="°C">°C</option><option value="°F">°F</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.tracks_reagent_changes}
                     onChange={(e) => setForm((f) => ({ ...f, tracks_reagent_changes: e.target.checked }))}
                     data-testid="equipment-tracks-reagents-input" className="h-4 w-4 accent-[#134e4a]" />
              Track reagent changes on this instrument (processors and stainers)
            </label>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        data-testid="equipment-notes-input" className="mt-1 min-h-[70px] border-slate-300" />
            </div>
            <Button type="submit" disabled={busy === "create"} data-testid="create-equipment-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "create" ? "Saving…" : "Add to register"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!checkTask} onOpenChange={(v) => !v && setCheckTask(null)}>
        <DialogContent data-testid="pm-complete-dialog">
          <DialogHeader>
            <DialogTitle>{checkTask?.task}</DialogTitle>
            <DialogDescription>
              {checkTask?.equipment_name} · {checkTask?.frequency}. Logged against your name and the current time.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={completeCheck} className="space-y-3">
            {checkTask?.records_temperature && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">
                  Temperature reading ({checkTask.temp_unit}) — acceptable {checkTask.temp_min ?? "—"} to {checkTask.temp_max ?? "—"}
                </Label>
                <Input type="number" step="any" value={checkForm.temperature} required
                       onChange={(e) => setCheckForm((f) => ({ ...f, temperature: e.target.value }))}
                       data-testid="pm-temperature-input" className="mt-1 h-11 border-slate-300" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Comments</Label>
              <Textarea value={checkForm.comments} onChange={(e) => setCheckForm((f) => ({ ...f, comments: e.target.value }))}
                        data-testid="pm-comments-input" className="mt-1 min-h-[80px] border-slate-300"
                        placeholder="Readings, consumables replaced, anything unusual." />
            </div>
            <Button type="submit" disabled={busy === "check"} data-testid="save-pm-completion-button"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "check" ? "Saving…" : "Log as done"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
